import { useTranslation } from 'react-i18next';
import type { ChatSessionMeta } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { cn, formatDate, formatUptime } from '@/lib/utils';

interface Props {
  sessions: ChatSessionMeta[];
  selectedId: string | null;
  onSelect: (sessionId: string) => void;
}

const statusVariant = (s: ChatSessionMeta['status']): 'success' | 'secondary' | 'warning' => {
  if (s === 'active') return 'success';
  if (s === 'expired') return 'warning';
  return 'secondary';
};

export function SessionList({ sessions, selectedId, onSelect }: Props): JSX.Element {
  const { t } = useTranslation();
  if (sessions.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        {t('chat.session.empty')}
      </p>
    );
  }
  return (
    <div className="space-y-1 p-1">
      {sessions.map((s) => (
        <button
          key={s.sessionId}
          type="button"
          onClick={() => onSelect(s.sessionId)}
          className={cn(
            'w-full rounded-md border p-2 text-left text-sm transition-colors',
            selectedId === s.sessionId
              ? 'border-primary bg-accent'
              : 'hover:bg-accent/50',
          )}
        >
          <div className="flex items-center gap-2">
            <Badge variant={statusVariant(s.status)} className="text-[10px]">
              {s.status}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {s.kind}
            </Badge>
            <span className="ml-auto text-[10px] text-muted-foreground">
              {formatUptime(Date.now() / 1000 - s.lastActivityAt)}
            </span>
          </div>
          <div className="mt-1 line-clamp-1 font-medium">
            {s.title ?? <span className="italic text-muted-foreground">{t('chat.session.untitled')}</span>}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{s.messageCount} {t('chat.session.msg')}</span>
            <span>·</span>
            <span>{s.tokensIn + s.tokensOut} {t('chat.session.tokens')}</span>
          </div>
          <div className="mt-1 truncate font-mono text-[10px] text-muted-foreground">
            {formatDate(s.createdAt)} · {s.sessionId}
          </div>
        </button>
      ))}
    </div>
  );
}
